import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaPlus, FaTimes, FaCheck } from "react-icons/fa";
import { useGetTaskAssigneesQuery } from "../../store";
import useAuth from "../../Hooks/useAuth";
import UserChip from "./atoms/UserChip";
import { useNestedOverlay } from "./nestedOverlay";
import { initials, userName } from "./taskConstants";

// Contract 1.8: işin atananları (en fazla 10). İsim etiketlerinde × ile çıkarma,
// yanında + ile kişi arayıp ekleme. onChange(ids) tam listeyi alır.
// Satır içinde (liste, alt iş) kullanılırken tıklamalar satırı açmaz.

export const MAX_ASSIGNEES = 10;
const stop = (e) => e.stopPropagation();
const PICKER_WIDTH = 256;

function AssigneePicker({ anchorRef, selectedIds, onPick, onClose }) {
  const { auth } = useAuth();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState(null);
  const menuRef = useRef(null);
  useNestedOverlay(true);
  const { data, isFetching } = useGetTaskAssigneesQuery({ token: auth?.accessToken, search: query });
  const users = data?.data?.users || [];

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useLayoutEffect(() => {
    const place = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;
      const left = Math.max(8, Math.min(r.left, vw - PICKER_WIDTH - 8));
      const below = vh - r.bottom;
      setPos(below >= 280 || below >= r.top ? { left, top: r.bottom + 4 } : { left, bottom: vh - r.top + 4 });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [anchorRef, onClose]);

  if (!pos) return null;
  return createPortal(
    <div
      ref={menuRef}
      role="dialog"
      aria-label="Kişi ata"
      data-testid="assignee-picker"
      onClick={stop}
      className="fixed z-[10000] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
      style={{ ...pos, width: PICKER_WIDTH }}
    >
      <div className="p-2 border-b border-gray-100">
        <input
          autoFocus
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kişi ara..."
          className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <ul className="max-h-60 overflow-y-auto py-1">
        {isFetching && !users.length ? (
          <li className="px-3 py-2 text-xs text-gray-400">Yükleniyor...</li>
        ) : !users.length ? (
          <li className="px-3 py-2 text-xs text-gray-400">Kişi bulunamadı</li>
        ) : (
          users.map((u) => {
            const taken = selectedIds.includes(u._id);
            return (
              <li key={u._id}>
                <button
                  type="button"
                  disabled={taken}
                  onClick={() => onPick(u)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-transparent"
                >
                  <UserChip user={u} size="sm" />
                  {taken && <FaCheck className="ml-auto w-3 h-3 text-main" aria-label="Atanmış" />}
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>,
    document.body
  );
}

// users: UserRef[]; canEdit: × ve + görünür; onChange(ids) Promise döndürebilir.
// emptyText: atanan yokken (salt okunur) yazı. showNames=false → yalnız baş harfler.
function AssigneeChips({ users = [], canEdit = false, onChange, size = "sm", showNames = true, emptyText = "Atanmamış" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const plusRef = useRef(null);
  const ids = users.map((u) => u._id);

  const save = async (next) => {
    if (!onChange || busy) return;
    setBusy(true);
    try {
      await onChange(next);
    } finally {
      setBusy(false);
    }
  };

  if (!users.length && !canEdit) return <span className="text-xs text-gray-400">{emptyText}</span>;

  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="assignee-chips" onClick={canEdit ? stop : undefined}>
      {users.map((u) =>
        showNames ? (
          <span
            key={u._id}
            data-testid="assignee-chip"
            className="inline-flex items-center gap-1 max-w-[11rem] rounded-full bg-gray-100 pl-0.5 pr-1.5 py-0.5"
          >
            <UserChip user={u} size={size} />
            {canEdit && (
              <button
                type="button"
                disabled={busy}
                onClick={() => save(ids.filter((id) => id !== u._id))}
                aria-label={`${userName(u)} atamasını kaldır`}
                title="Atamayı kaldır"
                className="shrink-0 rounded-full p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
              >
                <FaTimes className="w-2.5 h-2.5" aria-hidden="true" />
              </button>
            )}
          </span>
        ) : (
          <span key={u._id} title={userName(u)}>
            <UserChip user={u} size={size} showName={false} />
          </span>
        )
      )}
      {canEdit && ids.length < MAX_ASSIGNEES && (
        <>
          <button
            ref={plusRef}
            type="button"
            disabled={busy}
            onClick={() => setOpen((v) => !v)}
            aria-label="Kişi ata"
            title="Kişi ata"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-gray-300 text-gray-500 hover:text-main hover:border-main disabled:opacity-40"
          >
            <FaPlus className="w-2.5 h-2.5" aria-hidden="true" />
          </button>
          {open && (
            <AssigneePicker
              anchorRef={plusRef}
              selectedIds={ids}
              onClose={() => setOpen(false)}
              onPick={(u) => {
                setOpen(false);
                save([...ids, u._id]);
              }}
            />
          )}
        </>
      )}
      {!users.length && canEdit && <span className="text-xs text-gray-400">{emptyText}</span>}
    </div>
  );
}

// Panoda kart: üst üste baş harfler, fazlası "+N".
export function AssigneeAvatars({ users = [], max = 3 }) {
  if (!users.length) return null;
  const shown = users.slice(0, max);
  const extra = users.length - shown.length;
  return (
    <span className="inline-flex items-center -space-x-1.5" title={users.map(userName).join(", ")} data-testid="assignee-avatars">
      {shown.map((u) => (
        <span
          key={u._id}
          className={`w-5 h-5 rounded-full ring-2 ring-white flex items-center justify-center text-[10px] font-SemiBold ${
            u.isActive === false ? "bg-gray-200 text-gray-500" : "bg-primary text-main"
          }`}
          aria-hidden="true"
        >
          {initials(u)}
        </span>
      ))}
      {extra > 0 && (
        <span className="w-5 h-5 rounded-full ring-2 ring-white bg-gray-200 text-gray-600 flex items-center justify-center text-[10px] font-SemiBold">
          +{extra}
        </span>
      )}
    </span>
  );
}

export default AssigneeChips;
